import { FundingMeta, ProductId } from '@services/MarppleShop/features/ProductMeta';
import { FundingContractEntity } from './funding-contract-entity.ts';
import { Money } from '../../../../../../shared/features/domain/value-objects/Money.ts';
import { FundingCreator } from '@services/MarppleShop/features/Funding/domain/value-objects/funding-creator.ts';
import { FundingDetails } from '@services/MarppleShop/features/Funding/domain/value-objects/funding-details.ts';

export interface FundingEntityProps {
  productId: ProductId;
  fundingMeta: FundingMeta;
  fundingCreator: FundingCreator;
  fundingDetails: FundingDetails;
  createdAt?: Date;
  updatedAt?: Date;
}

export class FundingEntity {
  public readonly productId: ProductId;
  public readonly fundingMeta: FundingMeta;
  public readonly fundingCreator: FundingCreator;
  public readonly fundingDetails: FundingDetails;

  public readonly createdAt: Date;
  private _updatedAt: Date;

  constructor(props: FundingEntityProps) {
    this.productId = props.productId;
    this.fundingMeta = props.fundingMeta;
    this.fundingCreator = props.fundingCreator;
    this.fundingDetails = props.fundingDetails;

    this.createdAt = props.createdAt || new Date();
    this._updatedAt = props.updatedAt || new Date();
  }

  /**
   * 모든 계약을 집계하여 현재 모금액 계산
   */
  calculateCurrentAmount(contracts: FundingContractEntity[]): Money {
    return contracts.reduce((total, contract) => {
      return total.add(contract.getEffectiveAmount());
    }, Money.of(0));
  }

  /**
   * 펀딩 진행률 계산
   */
  calculateProgressPercentage(contracts: FundingContractEntity[]): number {
    const currentAmount = this.calculateCurrentAmount(contracts);
    const targetAmount = this.fundingMeta.getTargetAmount();
    return Math.round((currentAmount.getAmount() / targetAmount) * 100);
  }

  /**
   * 목표 달성 여부 확인
   */
  isTargetAchieved(contracts: FundingContractEntity[]): boolean {
    const currentAmount = this.calculateCurrentAmount(contracts);
    return currentAmount.getAmount() >= this.fundingMeta.getTargetAmount();
  }

  /**
   * 활성 후원자 수 계산
   */
  countActiveSupporters(contracts: FundingContractEntity[]): number {
    return contracts.filter((contract) => !contract.isCancelled()).length;
  }

  /**
   * 남은 모금액 계산
   */
  getRemainingAmount(contracts: FundingContractEntity[]): Money {
    const currentAmount = this.calculateCurrentAmount(contracts);
    const targetAmount = this.fundingMeta.getTargetAmount();
    const remaining = targetAmount - currentAmount.getAmount();
    return Money.of(Math.max(0, remaining));
  }

  /**
   * TODO: 펀딩 상품 주소
   */

  /**
   * TODO: 펀딩 크리에이터 샵 주소
   */

  /**
   * TODO: 펀딩 상태
   */

  getOrThrow(): ProductId {
    if (!this.productId) {
      throw new Error('초기화 되지 않은 FundingEntity 입니다.');
    }
    return this.productId;
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  static create(props: Omit<FundingEntityProps, 'id'>): FundingEntity {
    return new FundingEntity(props);
  }

  static from(props: FundingEntityProps): FundingEntity {
    return new FundingEntity(props);
  }
}
